import _ from "lodash";
import {
  getProductData,
  getProductHeadline,
  getProductLabel,
  getProductName,
  getStageList,
  isContractProduct,
  isDocumentProduct,
} from "../Product/ProductService/productService";
import { STAGE_INCOMPLETE } from "../Stages/StagesTraffic";
import * as documentStages from "../Stages/StagesDocument";
import { stageMetaDataDocument } from "../Stages/StagesDocument";
import * as contractStages from "../Stages/StagesCase";
import { stageMetaDataCase } from "../Stages/StagesCase";
import { translate } from "../Translations/translatorService";
import { BackofficeUser } from "../../types/BackofficeUser";
import { Hit } from "@algolia/client-search";
import { getPartnerDisplayName, resolvePartnerSlug } from "../partnerReferenceService";
import { RuntimePartnerReference } from "../../types/PartnerReference";

const getAccountManagersRefinable = (accountManagers: Array<BackofficeUser>) =>
  _.map(accountManagers, (accountManager) => {
    return {
      label: accountManager.person.fullname,
      value: accountManager.id + "",
    };
  });

const getLawyersRefinable = (lawyers: Array<BackofficeUser>) =>
  _.map(lawyers, (lawyer) => {
    return {
      label: lawyer.person.fullname,
      value: lawyer.id + "",
    };
  });

const getPartnersRefinable = (activePartners: RuntimePartnerReference[]) =>
  activePartners
    .map((partner) => {
      const partnerSlug = resolvePartnerSlug(partner);

      if (!partnerSlug) {
        return null;
      }

      return {
        label: getPartnerDisplayName(partner),
        value: partnerSlug,
      };
    })
    .filter((partner): partner is { label: string; value: string } => partner !== null);

export const getBoardHeadline = (productUrlPart: string) => {
  switch (productUrlPart) {
    case "dokumente":
    case "vertragsrecht":
      return _.upperFirst(productUrlPart);
    case "mietrecht":
      return _.upperFirst(productUrlPart);
    default:
      return getProductHeadline(productUrlPart);
  }
};

export const getStagesForBoard = (productUrlPart: string) => {
  let stages;
  switch (productUrlPart) {
    case "dokumente":
      stages = stageMetaDataDocument;
      break;
    case "vertragsrecht":
      stages = stageMetaDataCase;
      break;
    case "mietrecht":
      stages = stageMetaDataCase;
      break;
    default:
      stages = getStageList(getProductName(productUrlPart));
      break;
  }
  return _.keyBy(_.filter(stages, "showInBoard"), "stage");
};

export const getRefinements = ({
  accountManagers,
  lawyers,
  activePartners,
}: {
  accountManagers: Array<BackofficeUser>;
  lawyers: Array<BackofficeUser>;
  activePartners: RuntimePartnerReference[];
}) => {
  const lawyersRefinable = getLawyersRefinable(lawyers);
  const accountManagersRefinable = getAccountManagersRefinable(accountManagers);
  const partnersRefinable = getPartnersRefinable(activePartners);

  const litigationRefinables = [
    {
      attribute: {
        name: "responsibleLawyer",
        label: "Anwalt",
      },
      staticItems: lawyersRefinable,
    },
    {
      attribute: {
        name: "accountManager",
        label: "Kundenberater",
      },
      staticItems: accountManagersRefinable,
    },
    {
      attribute: {
        name: "caseCategory",
        label: "Kategorie",
      },
      staticItems: [
        {
          label: translate("abstractCase.caseCategory.values.enforceClaim"),
          value: "enforceClaim",
        },
        {
          label: translate("abstractCase.caseCategory.values.defendClaim"),
          value: "defendClaim",
        },
        {
          label: translate("abstractCase.caseCategory.values.advisory"),
          value: "advisory",
        },
      ],
    },

    {
      attribute: {
        name: "isHigherInstanceCase",
        label: "Instanz",
      },
      staticItems: [
        {
          label: "Höhere Instanz",
          value: "true",
        },
      ],
    },

    {
      attribute: {
        name: "partner",
        label: "Partner",
      },
      staticItems: partnersRefinable,
    },
  ];

  const contractLawRefinements = [
    ...litigationRefinables,
    {
      attribute: {
        name: "product",
        label: "Vertragsart",
      },
      staticItems: [
        {
          label: translate("eventContract.label"),
          value: "eventContract",
        },
        {
          label: translate("membershipContract.label"),
          value: "membershipContract",
        },
        {
          label: translate("otherContract.label"),
          value: "otherContract",
        },
        {
          label: translate("providerContract.label"),
          value: "providerContract",
        },
        {
          label: translate("purchaseContract.label"),
          value: "purchaseContract",
        },
        {
          label: translate("serviceContract.label"),
          value: "serviceContract",
        },
        {
          label: translate("subscriptionContract.label"),
          value: "subscriptionContract",
        },
        {
          label: translate("travelContract.label"),
          value: "travelContract",
        },
        {
          label: translate("vehicleContract.label"),
          value: "vehicleContract",
        },
      ],
    },
  ];

  return {
    scheidung: [
      {
        attribute: {
          name: "paymentType",
          label: "Zahlart",
        },
        staticItems: [
          { label: "VKH", value: "processCostSupport" },
          { label: "Rechnung", value: "private" },
          { label: "Raten", value: "installment" },
        ],
      },
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    abfindung: [
      {
        attribute: {
          name: "paymentType",
          label: "Zahlart",
        },
        staticItems: [
          { label: "NWNF", value: "nwnf" },
          { label: "Rechnung", value: "private" },
          { label: "RSV", value: "insurance" },
        ],
      },
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    verkehr: [
      {
        attribute: {
          name: "paymentType",
          label: "Zahlart",
        },
        staticItems: [
          { label: "Rechnung", value: "private" },
          { label: "RSV", value: "insurance" },
          { label: "ARAG", value: "backwards_insurance" },
        ],
      },
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    verkehrsunfall: [
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "paymentType",
          label: "Zahlart",
        },
        staticItems: [
          { label: "Selbstzahler", value: "private" },
          { label: "RSV", value: "insurance" },
        ],
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    kurzarbeit: [
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    dokumente: [
      {
        attribute: {
          name: "product",
          label: "Dokumententyp",
        },
        staticItems: [
          {
            label: translate("patientDecree.label"),
            value: "patientDecree",
          },
          {
            label: translate("powerOfAttorney.label"),
            value: "powerOfAttorney",
          },
          {
            label: translate("death.label"),
            value: "death",
          },
          {
            label: translate("alimony.label"),
            value: "alimony",
          },
        ],
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    vertragsrecht: contractLawRefinements,
    datenweitergabe: litigationRefinables,
    familie: [
      {
        attribute: {
          name: "responsibleLawyer",
          label: "Anwalt",
        },
        staticItems: lawyersRefinable,
      },
      {
        attribute: {
          name: "accountManager",
          label: "Kundenberater",
        },
        staticItems: accountManagersRefinable,
      },
      {
        attribute: {
          name: "partner",
          label: "Partner",
        },
        staticItems: partnersRefinable,
      },
    ],
    mietrecht: litigationRefinables,
    "allgemeines-zivilrecht": litigationRefinables,
    arbeitsvertrag: litigationRefinables,
    beamtenrecht: litigationRefinables,
  };
};

export const getLabelFromHit = (hit: Hit<any>) => {
  if (!hit.gender || !hit.givenName || !hit.familyName) {
    return hit.email;
  }
  return `${translate("person.gender.values." + hit.gender)} ${hit.givenName} ${hit.familyName}`;
};

export const translatePaymentType = ({
  paymentType,
  product,
  stage,
}: {
  paymentType: string;
  product: string;
  stage: number;
}) => {
  switch (product) {
    case "divorce":
      switch (paymentType) {
        case "processCostSupport":
        case "process_cost_support":
          return "VKH";
        case "installment":
          return "Raten";
        case "private":
          return "Rechnung";
        case "insurance":
          return "RSV";
        default:
          return paymentType || "N/A";
      }
    case "settlement":
      switch (paymentType) {
        case "private":
          return "SZ";
        case "insurance":
          return "RSV";
        case "nwnf":
          return "NWNF";
        default:
          return paymentType || "N/A";
      }
    case "traffic":
      switch (paymentType) {
        case "private":
          return "SZ";
        case "insurance":
          return "RSV";
        case "backwards_insurance":
          return "ARAG Verso";
        default:
          if (!paymentType) {
            return stage < STAGE_INCOMPLETE ? "N/A" : "keine RSV";
          }
          return paymentType;
      }
    case "trafficAccident":
      switch (paymentType) {
        case "private":
          return "SZ";
        case "insurance":
          return "RSV";
        default:
          return paymentType || "N/A";
      }
    case "familyLawCase":
    case "generalCivilLaw":
    case "civilServiceLaw":
    case "employmentContract":
      switch (paymentType) {
        default:
        case "insurance":
          return "RSV";
      }
    default:
      return paymentType || "-";
  }
};

export const getBadgeContent = (hit: Hit<any>) => {
  const productClassName = hit.product;
  if (isDocumentProduct(productClassName)) {
    return getProductLabel(productClassName);
  }
  if (isContractProduct(productClassName)) {
    return _.upperFirst(getProductData(productClassName, "urlPart"));
  }
  return translatePaymentType(hit);
};

export const getFilterString = (productUrlPart: string, caseTypeUrlPart: string) => {
  const caseType = _.get({ verloren: "lost", gewonnen: "won", inaktiv: "inactive" }, caseTypeUrlPart, "open");
  let productFilter;
  let stage;
  switch (productUrlPart) {
    case "dokumente":
      productFilter = `product:patientDecree OR product:powerOfAttorney OR product:death OR product:alimony`;
      stage = documentStages.STAGE_INCOMPLETE;
      break;
    case "vertragsrecht":
      productFilter =
        "product:eventContract OR product:membershipContract OR product:otherContract" +
        " OR product:providerContract OR product:purchaseContract OR product:serviceContract" +
        " OR product:subscriptionContract OR product:travelContract OR product:vehicleContract";
      stage = contractStages.STAGE_DATA_COMPLETED;
      break;
    case "mietrecht":
      productFilter = "product:rentalContract";
      stage = contractStages.STAGE_DATA_COMPLETED;
      break;
    default:
      const productClassName = getProductName(productUrlPart);
      stage = getProductData(productClassName, "minimumCaseStage");
      productFilter = `product:${productClassName} OR product:${_.snakeCase(productClassName)}`;
      break;
  }
  return `(${productFilter}) AND caseType:${caseType} AND stage>=${stage}`;
};
